import Table, { TableCell, TableHeaderCell, TableLinkRow } from '@/components/Table';
import TeamImage from 'components/TeamImage';
import styles from './styles/StandingsTable.module.scss';
import TextLink, { TextColorVariant, TextLinkSize } from './TextLink';

export default function StandingsTable<
  T extends {
    place?: number | string;
    team: {
      id: string;
      image?: string;
      name: string;
      webPath: string;
    };
    standing: {
      leagueRecord: string;
      leagueGamesBack?: string;
      overallRecord: string;
      overallWinPercentage?: string;
      homeRecord?: string;
      awayRecord?: string;
    };
  }
>({
  isHighlighted,
  teamStandings,
  overallRecordBeforeLeague = false
}: {
  isHighlighted?: (teamStanding: T) => boolean;
  teamStandings: T[];
  overallRecordBeforeLeague?: boolean;
}) {
  const hasPlaces = teamStandings.every(({ place }) => !!place);
  const hasGamesBack = teamStandings.some(({ standing: { leagueGamesBack } }) => !!leagueGamesBack);
  const hasWinPercentange = teamStandings.some(({ standing: { overallWinPercentage } }) => !!overallWinPercentage);
  const hasHomeRecords = teamStandings.some(({ standing: { homeRecord } }) => !!homeRecord);
  const hasAwayRecords = teamStandings.some(({ standing: { awayRecord } }) => !!awayRecord);

  return (
    <Table
      className={hasPlaces ? styles.TableWithPlaces : styles.TableWithoutPlaces}
      header={
        <>
          {hasPlaces && <TableHeaderCell>#</TableHeaderCell>}
          <TableHeaderCell>Team</TableHeaderCell>
          {overallRecordBeforeLeague ? (
            <>
              <TableHeaderCell align="right">OVR</TableHeaderCell>
              {hasGamesBack && <TableHeaderCell align="right">GB</TableHeaderCell>}
              <TableHeaderCell align="right">W-L</TableHeaderCell>
            </>
          ) : (
            <>
              <TableHeaderCell align="right">W-L</TableHeaderCell>
              {hasGamesBack && <TableHeaderCell align="right">GB</TableHeaderCell>}
              <TableHeaderCell align="right">OVR</TableHeaderCell>
            </>
          )}
          {hasWinPercentange && <TableHeaderCell align="right">PCT</TableHeaderCell>}
          {hasHomeRecords && <TableHeaderCell align="right">Home</TableHeaderCell>}
          {hasAwayRecords && <TableHeaderCell align="right">Away</TableHeaderCell>}
        </>
      }
    >
      {teamStandings.map(teamStanding => {
        const { place, team, standing } = teamStanding;

        return (
          <TableLinkRow key={team.id} highlighted={isHighlighted?.(teamStanding)} href={team.webPath}>
            {hasPlaces && <TableCell>{place}</TableCell>}
            <TableCell>
              <TextLink href={team.webPath} color={TextColorVariant.BLACK} size={TextLinkSize.SMALL}>
                <TeamImage className={styles.TeamImage} src={team.image} alt={team.name} />
                {team.name}
              </TextLink>
            </TableCell>
            {overallRecordBeforeLeague ? (
              <>
                <TableCell align="right">{standing.overallRecord}</TableCell>
                {hasGamesBack && <TableCell align="right">{standing.leagueGamesBack}</TableCell>}
                <TableCell align="right">{standing.leagueRecord}</TableCell>
              </>
            ) : (
              <>
                <TableCell align="right">{standing.leagueRecord}</TableCell>
                {hasGamesBack && <TableCell align="right">{standing.leagueGamesBack}</TableCell>}
                <TableCell align="right">{standing.overallRecord}</TableCell>
              </>
            )}
            {hasWinPercentange && <TableCell align="right">{standing.overallWinPercentage}</TableCell>}
            {hasHomeRecords && <TableCell align="right">{standing.homeRecord}</TableCell>}
            {hasAwayRecords && <TableCell align="right">{standing.awayRecord}</TableCell>}
          </TableLinkRow>
        );
      })}
    </Table>
  );
}
